import { useRef, useState } from 'react';
import { Button } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { FileStage } from '../OcifBulkUpload/types';
import { ValidationIndicator } from '../OcifBulkUpload/StatusBadge';
import Loader from '../../Loader';
import Icon from 'src/components/Icon';

interface InventoryDropZoneProps {
  stage: FileStage;
  onFileSelected: (file: File) => void;
  onValidate: () => void;
  /** Retry with a freshly picked file — see the Retry button for why it never re-sends the old one. */
  onRetry: (file: File) => void;
  onReplace: () => void;
  onDownload?: () => void;
  downloading?: boolean;
}

export function InventoryDropZone({ stage, onFileSelected, onValidate, onRetry, onReplace, onDownload, downloading }: InventoryDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const retryInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    e.target.value = '';
  };

  const handleRetryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onRetry(file);
    e.target.value = '';
  };

  if (stage.file) {
    const displayName = stage.preloadedFileName ?? stage.file.name;
    const isPreloaded = !!stage.preloadedFileName;
    return (
      <div className="border rounded p-2 bg-light alert alert-info" style={{ fontSize: '0.8rem' }}>
        <div className="flex items-center gap-2 mb-1" style={{ overflow: 'hidden', justifyContent: 'center' }}>
          <Icon icon="FileXls" className="text-primary" style={{ flexShrink: 0 }} />
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{displayName}</strong>
        </div>
        {stage.uploadedAt && (
          <div className="text-muted mb-2 text-center" style={{ fontSize: '0.72rem' }}>
            <Trans>Last updated:</Trans> {new Date(stage.uploadedAt).toLocaleString()}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap" style={{ justifyContent: 'center' }}>
          {stage.validationState === 'idle' && !isPreloaded && (
            <Button color="primary" size="sm" style={{ fontSize: '0.75rem' }} onClick={onValidate}>
              <Trans>Validate and Submit</Trans>
            </Button>
          )}
          {stage.validationState === 'error' && (
            <>
              {/* Retry opens the picker instead of re-sending stage.file. A selected File is a snapshot:
                  once the filer fixes the workbook on disk, the browser must refuse to read it (File API
                  "snapshot state") — Firefox instead sent a malformed request. Picking the file again is
                  the only way to upload the corrected version. */}
              <input ref={retryInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleRetryInputChange} />
              <Button color="warning" size="sm" style={{ fontSize: '0.75rem' }} onClick={() => retryInputRef.current?.click()}>
                <Trans>Retry</Trans>
              </Button>
            </>
          )}
          {stage.validationState !== 'idle' && (
            <div className="mt-1 text-center w-full">
              <ValidationIndicator stage={stage} />
            </div>
          )}
          {isPreloaded && onDownload && (
            <Button color="primary" size="sm" outline style={{ fontSize: '0.75rem' }} disabled={downloading} onClick={onDownload}>
              {downloading ? <Loader spinner sm inline size={12} /> : <Icon icon="Download" />}
              <Trans>Download</Trans>
            </Button>
          )}
          <Button color="secondary" size="sm" outline style={{ fontSize: '0.75rem' }} onClick={onReplace}>
            ↩ <Trans>Replace</Trans>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleInputChange} />
      <div
        className={`bulk-drop-zone p-2 text-center${isDragging ? ' is-dragging' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragEnter={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <span className="text-primary font-bold small">
          <Trans>Select file</Trans>
        </span>{' '}
        <span className="text-muted small">
          <Trans>or drag here</Trans>
        </span>
      </div>
    </>
  );
}
